import { FileCopyOutlined } from '@mui/icons-material';
import { ListItemIcon, ListItemText } from '@mui/material';

import { graphql, type DocumentType } from '@/gql';

import { useMutation } from '@/hooks/mutationWithSnackbar';

import { addNewFileToCache } from '@/modules/files/utils/fileUpdateFunctions';

import { MaybeUsableMenuItem } from './MaybeUnusableMenuItem';

const DUPLICATE_FILE_MUTATION = graphql(`
  mutation DuplicateFile($data: DuplicateFileInput) {
    duplicateFile(params: $data) {
      file {
        ...fileFields
      }
    }
  }
`);

const DUPLICATE_FILE_MENU_ITEM_FILE_FRAGMENT = graphql(`
  fragment duplicateFileMenuItemFileFields on File {
    ...fileFields
  }
`);

interface Props {
  file: DocumentType<typeof DUPLICATE_FILE_MENU_ITEM_FILE_FRAGMENT>;
  rootOwnerId: string;
  canEdit: boolean;
  canLockRoot: boolean;
  unusableExplanation: string | null;
  onClose: () => void;
}

export const DuplicateFileMenuItem = ({
  file,
  rootOwnerId,
  canEdit,
  canLockRoot,
  unusableExplanation,
  onClose,
}: Props) => {
  const { _id: fileId, filename } = file;

  const [duplicateFile] = useMutation(DUPLICATE_FILE_MUTATION, {
    errorMessage: `Couldn't duplicate file`,
    optimisticResponse: {
      duplicateFile: {
        __typename: 'DuplicateFileResult',
        file: {
          ...file,
          _id: 'temp',
          filename: `Copy of ${filename}`,
        },
      },
    },
    update(cache, { data }) {
      const duplicatedFile = data?.duplicateFile?.file;

      addNewFileToCache(cache, duplicatedFile, rootOwnerId);
    },
  });

  return (
    <MaybeUsableMenuItem
      isUsable={canEdit}
      isExpectedUsable={canLockRoot}
      unusableExplanation={unusableExplanation}
      onClick={() => {
        onClose();
        duplicateFile({ variables: { data: { file: fileId } } });
      }}
    >
      <ListItemIcon>
        <FileCopyOutlined />
      </ListItemIcon>
      <ListItemText primary="Make a copy" />
    </MaybeUsableMenuItem>
  );
};
